import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { landingFor } from '../config/permissions.js';

/**
 * Role-gated route guard. Wrap feature routes that only certain roles may view:
 *
 *   <Route element={<RoleRoute allow={[ROLES.OPERATOR_ADMIN]} />}>
 *     <Route path="/admin/..." element={<AdminPage />} />
 *   </Route>
 *
 * - Not authenticated  → redirect to /login (preserving the target).
 * - Authenticated, wrong role → redirect to the user's own landing dashboard
 *   (never a dead-end 403). This also catches a stale post-login return-path
 *   that points at a page the role can't open.
 * - Allowed → render the nested route.
 */
export default function RoleRoute({ allow = [] }) {
  const { isAuthenticated, loading, hasAnyRole, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (allow.length > 0 && !hasAnyRole(allow)) {
    const home = landingFor(user?.role);
    return <Navigate to={home ? `/${home}` : '/'} replace />;
  }

  return <Outlet />;
}
